import React from "react";
import { DateTime } from "luxon";
import { TVendorAmlEvidence } from "../../../../domain/codecs/VendorAmlEvidence";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { Text } from "../BuildingBlocks/Text";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { WeightBold } from "../WeightBold/WeightBold";
import { WeightRegular } from "../WeightRegular/WeightRegular";
import { CRMIconBinary } from "../CRMIconBinary/CRMIconBinary";
import { CRMLink } from "../CRMLink/CRMLink";

type TCRMListingVendorAmlComponentProps = {
    evidence: TVendorAmlEvidence | undefined;
    isLegalSeller: boolean;
    isDeceased: boolean;
};

const formatDate = (value: string): string =>
    DateTime.fromISO(value).toFormat("d MMM yyyy");

const summarise = (evidence: TVendorAmlEvidence | undefined): string => {
    if (evidence === undefined || evidence.id_check_id === null) {
        return "No AML check on file";
    }

    if (evidence.is_established !== true) {
        return `Identity ${evidence.identity_status}`;
    }

    if (evidence.has_expired === true) {
        return evidence.expires_at !== null
            ? `Screening expired ${formatDate(evidence.expires_at)}`
            : "Screening expired";
    }

    return evidence.peps_sanctions_status === "pass" && evidence.address_status === "pass"
        ? "Passed"
        : `PEPs ${evidence.peps_sanctions_status} · address ${evidence.address_status}`;
};

/**
 * One seller's AML position, derived from the linked Sail Legal case.
 *
 * **When to use:** in the seller-details form beside the member's ID sign-off. Renders
 * nothing for anyone who is not a living legal seller — an introducer, a keyholder, or the
 * deceased proprietor on a probate sale is owed no vendor CDD, so a red icon against them
 * would be a false alarm. 561 of 611 legal sellers on instructed listings are the deceased.
 *
 * @example
 * <CRMListingVendorAmlComponent evidence={e} isLegalSeller={true} isDeceased={false} />
 */
export const CRMListingVendorAmlComponent = (props: TCRMListingVendorAmlComponentProps): JSX.Element | null => {
    if (props.isLegalSeller !== true || props.isDeceased === true) {
        return null;
    }

    const evidence = props.evidence;
    const passed = evidence !== undefined
        && evidence.id_check_id !== null
        && evidence.is_established === true
        && evidence.has_expired !== true;

    return (
        <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center">
            <CRMIconBinary
                title={`Vendor AML — ${summarise(evidence)}`}
                completedState={passed}
                iconName="identity-badge"
                size="large"
            />

            <SpacingColumn spacing={CRMSpacing.TINY}>
                <FontQuicksand>
                    <Text size={CRMFontSizes.SMALL}>
                        <WeightBold>Vendor AML: </WeightBold>
                        <WeightRegular>{summarise(evidence)}</WeightRegular>
                    </Text>
                </FontQuicksand>

                {evidence?.via_case_id != null &&
                    <FontQuicksand>
                        <Text size={CRMFontSizes.SMALL}>
                            <CRMLink
                                href={`/crm/legal/cases/${evidence.via_case_id}`}
                                target="_blank"
                                linkStyle="normal"
                            >
                                <WeightRegular>
                                    {evidence.checked_at !== null
                                        ? `Checked ${formatDate(evidence.checked_at)} — Sail Legal case`
                                        : "Sail Legal case"}
                                </WeightRegular>
                            </CRMLink>
                        </Text>
                    </FontQuicksand>
                }
            </SpacingColumn>
        </SpacingRow>
    );
};
